import { Component, DestroyRef, effect, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';

import { LangPipe } from '@reforgium/presentia';

import { PdfTextLoaderService } from './pdf-text-loader.service';

type PageStatus = 'pending' | 'loading' | 'ready' | 'error';

interface PdfPageText {
  readonly number: number;
  readonly status: PageStatus;
  readonly lines: readonly string[];
}

@Component({
  selector: 'app-pdf-text-preview',
  imports: [LangPipe],
  template: `
    <div #viewport class="pdf-readable" [attr.lang]="language()">
      <h2 class="pdf-readable__heading">{{ 'common.accessibility.pdfTextVersion' | lang }}</h2>

      @if (error()) {
        <p class="pdf-readable__message">{{ 'common.accessibility.pdfError' | lang }}</p>
      }

      @for (page of pages(); track page.number) {
        <section class="pdf-readable__page" [attr.data-pdf-page]="page.number">
          <h3>{{ 'common.accessibility.pdfPage' | lang }} {{ page.number }}</h3>

          @if (page.status === 'ready') {
            @for (line of page.lines; track $index) {
              <p>{{ line }}</p>
            } @empty {
              <p class="pdf-readable__message">{{ 'common.accessibility.pdfNoText' | lang }}</p>
            }
          } @else if (page.status === 'error') {
            <p class="pdf-readable__message">{{ 'common.accessibility.pdfError' | lang }}</p>
          } @else {
            <p class="pdf-readable__message">{{ 'common.accessibility.pdfLoading' | lang }}</p>
          }
        </section>
      }
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex: 1 1 auto;
      min-width: 0;
      min-height: 0;
    }

    .pdf-readable {
      width: 100%;
      min-width: 0;
      overflow: auto;
      overscroll-behavior: contain;
      padding: 1rem;
      background: var(--surface-background, #eef3f8);
    }

    .pdf-readable__heading {
      max-width: 52rem;
      margin: 0 auto 0.7rem;
      color: var(--text-secondary, #52677f);
      font-size: 0.8rem;
      font-weight: 700;
    }

    .pdf-readable__page {
      box-sizing: border-box;
      width: min(100%, 52rem);
      min-height: min(70vh, 800px);
      margin: 0 auto 1rem;
      padding: clamp(1.2rem, 4vw, 2.5rem);
      background: var(--surface-neutral, #fff);
      box-shadow: 0 0.2rem 1rem rgb(17 40 75 / 10%);
      color: var(--text-primary, #1b2b40);
      line-height: 1.55;
      overflow-wrap: anywhere;
    }

    .pdf-readable__page h3 {
      margin: 0 0 1.5rem;
      font-size: 0.9rem;
      color: var(--text-secondary, #52677f);
    }

    .pdf-readable__page p {
      margin: 0 0 0.45rem;
      white-space: pre-wrap;
    }

    .pdf-readable__message {
      color: var(--text-secondary, #52677f);
    }
  `,
})
export class PdfTextPreviewComponent {
  readonly data = input.required<ArrayBuffer>();
  readonly language = input('ru');

  protected readonly pages = signal<readonly PdfPageText[]>([]);
  protected readonly error = signal(false);

  private readonly loader = inject(PdfTextLoaderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewport = viewChild<ElementRef<HTMLElement>>('viewport');
  private readonly requestedPages = new Set<number>();
  private pdf?: PDFDocumentProxy;
  private loadingTask?: PDFDocumentLoadingTask;
  private observer?: IntersectionObserver;
  private generation = 0;

  constructor() {
    effect(() => {
      void this.load(this.data());
    });

    this.destroyRef.onDestroy(() => {
      this.generation++;
      this.observer?.disconnect();
      void this.loadingTask?.destroy();
    });
  }

  private async load(data: ArrayBuffer): Promise<void> {
    const generation = ++this.generation;
    const previous = this.loadingTask;

    this.observer?.disconnect();
    this.requestedPages.clear();
    this.pages.set([]);
    this.error.set(false);
    this.pdf = undefined;
    await previous?.destroy();

    try {
      const task = await this.loader.open(data);

      if (generation !== this.generation) {
        await task.destroy();

        return;
      }

      this.loadingTask = task;
      const pdf = await task.promise;

      if (generation !== this.generation) {
        await task.destroy();

        return;
      }

      this.pdf = pdf;
      this.pages.set(
        Array.from({ length: pdf.numPages }, (_, index) => ({
          number: index + 1,
          status: 'pending' as const,
          lines: [],
        })),
      );

      window.setTimeout(() => this.observePages(generation), 0);
    } catch {
      if (generation === this.generation) this.error.set(true);
    }
  }

  private observePages(generation: number): void {
    if (generation !== this.generation) return;

    const viewport = this.viewport()?.nativeElement;

    if (!viewport) return;

    if (typeof IntersectionObserver === 'undefined') {
      for (const page of this.pages()) void this.loadPage(page.number, generation);

      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          this.observer?.unobserve(entry.target);
          const number = Number((entry.target as HTMLElement).dataset['pdfPage']);

          if (number) void this.loadPage(number, generation);
        }
      },
      { root: viewport, rootMargin: '600px 0px' },
    );

    for (const element of viewport.querySelectorAll<HTMLElement>('[data-pdf-page]')) {
      this.observer.observe(element);
    }
  }

  private async loadPage(number: number, generation: number): Promise<void> {
    if (!this.pdf || this.requestedPages.has(number)) return;

    this.requestedPages.add(number);
    this.updatePage(number, { status: 'loading' });

    try {
      const page = await this.pdf.getPage(number);
      const content = await page.getTextContent();
      const lines: string[] = [];
      let line = '';

      for (const item of content.items) {
        if (!('str' in item)) continue;

        line += item.str + ' ';

        if (item.hasEOL) {
          if (line.trim()) lines.push(line.trim());
          line = '';
        }
      }

      if (line.trim()) lines.push(line.trim());
      if (generation === this.generation) this.updatePage(number, { status: 'ready', lines });
    } catch {
      if (generation === this.generation) this.updatePage(number, { status: 'error' });
    }
  }

  private updatePage(number: number, change: Partial<PdfPageText>): void {
    this.pages.update((pages) => pages.map((page) => (page.number === number ? { ...page, ...change } : page)));
  }
}
